<template>
  <div class="blitz-game">
    <SharedProgressBar
      v-if="phase === 'playing'"
      :total-score="totalScore"
      :target="target"
      :players="players"
      :combo="combo"
      :won="gameWon"
    />

    <!-- Timer -->
    <div v-if="phase === 'playing'" class="timer-row">
      <div class="timer-track">
        <div
          class="timer-fill"
          :style="{ width: timerPercent + '%' }"
          :class="{ urgent: timeLeft <= 10 }"
        ></div>
      </div>
      <span class="timer-text" :class="{ urgent: timeLeft <= 10 }">{{ timeLeft }}s</span>
    </div>

    <!-- Score HUD -->
    <div v-if="phase === 'playing'" class="hud">
      <div class="hud-block">
        <span class="hud-val">{{ myScore }}</span>
        <span class="hud-lbl">Punkte</span>
      </div>
      <div class="hud-block combo-block" :class="{ active: combo >= 2 }">
        <span class="hud-val">{{ combo >= 2 ? `x${combo}` : '—' }}</span>
        <span class="hud-lbl">Combo</span>
      </div>
      <div class="hud-block">
        <span class="hud-val">{{ completedOrders }}</span>
        <span class="hud-lbl">Fertig</span>
      </div>
    </div>

    <!-- Current Order -->
    <div v-if="phase === 'playing'" class="order-card" :class="{ flash: orderFlash }">
      <div class="order-head">
        <span class="order-title">Bestellung #{{ orderCount }}</span>
        <span v-if="orderFlash" class="order-bonus">+50</span>
      </div>
      <div class="order-slots">
        <div
          v-for="(item, i) in currentOrder"
          :key="i"
          class="order-slot"
          :class="{ done: item.collected }"
        >
          <span class="slot-emoji">{{ item.emoji }}</span>
          <span v-if="item.collected" class="slot-check">✓</span>
        </div>
      </div>
    </div>

    <!-- Food Grid -->
    <div v-if="phase === 'playing'" class="food-grid" :class="{ shake: wrongShake }">
      <button
        v-for="(cell, idx) in grid"
        :key="cell.key"
        class="food-btn"
        :class="{
          correct: cell.flash === 'correct',
          wrong: cell.flash === 'wrong',
          used: cell.used,
        }"
        @click="tapCell(idx)"
      >
        <span class="btn-emoji">{{ cell.emoji }}</span>
      </button>
    </div>

    <!-- Idle -->
    <div v-if="phase === 'idle'" class="overlay">
      <div class="idle-hero">⚡</div>
      <h2 class="idle-title">Bestell-Blitz</h2>
      <p class="idle-sub">Erfülle Bestellungen im Blitztempo!</p>
      <p class="idle-hint">
        Oben siehst du eine Bestellung.<br />
        Tippe unten die passenden Zutaten –<br />
        je schneller, desto mehr Punkte!
      </p>
      <div class="btn-row">
        <button @click="startSolo" class="play-btn solo">Solo</button>
        <button @click="startMultiplayer" class="play-btn multi">Tisch-Modus</button>
      </div>
    </div>

    <!-- Lobby -->
    <div v-if="phase === 'lobby'" class="overlay">
      <div class="lobby-icon">⚡</div>
      <h2 class="lobby-title">Bestell-Blitz – Lobby</h2>
      <p class="lobby-sub">Warte, bis alle am Tisch beigetreten sind</p>

      <div class="lobby-grid">
        <div v-for="p in players" :key="p.id" class="lobby-chip">
          <div class="chip-avatar" :class="{ host: p.isHost }">
            {{ (p.name || '?')[0].toUpperCase() }}
          </div>
          <span class="chip-name">{{ p.name }}</span>
          <span v-if="p.isHost" class="chip-badge">HOST</span>
        </div>
      </div>

      <p class="lobby-info">
        {{ players.length }} Spieler · Ziel: {{ target }} Punkte · {{ duration }}s
      </p>

      <button v-if="isHost" @click="launchGame" class="play-btn solo lobby-start">
        Spiel starten ({{ players.length }} Spieler)
      </button>
      <p v-else class="lobby-wait">Warte auf Host...</p>
    </div>

    <!-- Result -->
    <div v-if="phase === 'result'" class="overlay">
      <div class="result-hero">{{ gameWon ? '🏆' : '⚡' }}</div>
      <h2 class="result-title">{{ gameWon ? 'Blitz-Meister!' : 'Guter Versuch!' }}</h2>
      <div class="result-stats">
        <div class="r-stat">
          <span class="r-val">{{ myScore }}</span>
          <span class="r-lbl">Punkte</span>
        </div>
        <div class="r-stat">
          <span class="r-val">{{ completedOrders }}</span>
          <span class="r-lbl">Bestellungen</span>
        </div>
        <div class="r-stat">
          <span class="r-val">{{ maxCombo }}</span>
          <span class="r-lbl">Max Combo</span>
        </div>
      </div>
      <div v-if="reward" class="reward-card">
        <p class="reward-text">{{ reward.message }}</p>
      </div>
      <button @click="$emit('close')" class="play-btn solo result-close">Schließen</button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import SharedProgressBar from './SharedProgressBar.vue'
import { gamificationService } from '@/services/api/gamificationService'

const props = defineProps({
  existingGameId: { type: String, default: null },
})
const emit = defineEmits(['close', 'apply-discount'])

const FOODS = [
  { emoji: '🍔', name: 'Burger' },
  { emoji: '🍟', name: 'Pommes' },
  { emoji: '🥤', name: 'Cola' },
  { emoji: '🍕', name: 'Pizza' },
  { emoji: '🌭', name: 'Hotdog' },
  { emoji: '🍗', name: 'Hähnchen' },
  { emoji: '🥗', name: 'Salat' },
  { emoji: '🍰', name: 'Kuchen' },
  { emoji: '🍩', name: 'Donut' },
  { emoji: '🍝', name: 'Pasta' },
  { emoji: '🥙', name: 'Wrap' },
  { emoji: '🍣', name: 'Sushi' },
]
const GRID_SIZE = 12

const phase = ref('idle')
const myScore = ref(0)
const totalScore = ref(0)
const target = ref(300)
const duration = ref(45)
const timeLeft = ref(45)
const combo = ref(0)
const maxCombo = ref(0)
const gameWon = ref(false)
const players = ref([])
const reward = ref(null)
const isHost = ref(false)
const orderCount = ref(0)
const completedOrders = ref(0)
const currentOrder = ref([])
const grid = ref([])
const orderFlash = ref(false)
const wrongShake = ref(false)

let gameSessionId = null
let timerInterval = null
let lobbyPollInterval = null
let lastSyncScore = 0
let cellKeyCounter = 0
const myId =
  localStorage.getItem('sessionUniqueId') ||
  localStorage.getItem('sessionId') ||
  ''

const timerPercent = computed(
  () => (timeLeft.value / Math.max(1, duration.value)) * 100,
)

function shuffle(arr) {
  const a = [...arr]
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

function makeGrid(orderItems) {
  const needed = orderItems.filter((i) => !i.collected).map((i) => i.emoji)
  const usedSet = new Set(needed)
  const cells = []

  for (const emoji of needed) {
    cells.push({ emoji, used: false, flash: null, key: ++cellKeyCounter })
  }

  const distractors = shuffle(FOODS.filter((f) => !usedSet.has(f.emoji)))
  let dIdx = 0
  while (cells.length < GRID_SIZE) {
    if (dIdx < distractors.length) {
      const d = distractors[dIdx++]
      cells.push({ emoji: d.emoji, used: false, flash: null, key: ++cellKeyCounter })
    } else {
      const f = FOODS[Math.floor(Math.random() * FOODS.length)]
      cells.push({ emoji: f.emoji, used: false, flash: null, key: ++cellKeyCounter })
    }
  }

  return shuffle(cells)
}

function newOrder() {
  const diff = completedOrders.value
  const size = Math.min(2 + Math.floor(diff / 2), 5)
  const picked = shuffle([...FOODS]).slice(0, size)
  orderCount.value++
  currentOrder.value = picked.map((f) => ({
    emoji: f.emoji,
    name: f.name,
    collected: false,
  }))
  grid.value = makeGrid(currentOrder.value)
}

function tapCell(idx) {
  const cell = grid.value[idx]
  if (!cell || cell.used || phase.value !== 'playing') return

  const needed = currentOrder.value.find(
    (o) => !o.collected && o.emoji === cell.emoji,
  )

  if (needed) {
    needed.collected = true
    cell.used = true
    cell.flash = 'correct'
    combo.value++
    maxCombo.value = Math.max(maxCombo.value, combo.value)

    const mult = 1 + Math.min(combo.value - 1, 9) * 0.2
    myScore.value += Math.round(10 * mult)

    if (navigator.vibrate) navigator.vibrate(15)
    setTimeout(() => {
      cell.flash = null
    }, 300)

    if (currentOrder.value.every((o) => o.collected)) {
      completeOrder()
    }
  } else {
    cell.flash = 'wrong'
    combo.value = 0
    myScore.value = Math.max(0, myScore.value - 5)
    wrongShake.value = true

    if (navigator.vibrate) navigator.vibrate([30, 20, 30])
    setTimeout(() => {
      cell.flash = null
    }, 400)
    setTimeout(() => {
      wrongShake.value = false
    }, 300)
  }
}

function completeOrder() {
  completedOrders.value++
  myScore.value += 50
  orderFlash.value = true
  setTimeout(() => {
    orderFlash.value = false
  }, 500)
  setTimeout(newOrder, 600)
}

/* ---- Lobby & Session ---- */

async function startSolo() {
  await createOrJoinSession()
  beginPlaying()
}

async function startMultiplayer() {
  await createOrJoinSession()
  phase.value = 'lobby'
  isHost.value = !props.existingGameId
  startLobbyPolling()
}

async function createOrJoinSession() {
  const tid = parseInt(localStorage.getItem('tableId')) || 0
  try {
    let data
    if (props.existingGameId) {
      const res = await gamificationService.joinMiniGame(
        props.existingGameId,
        { sessionIdentifier: myId, tableId: tid, playerName: 'Spieler' },
      )
      data = res.data
      isHost.value = false
    } else {
      const res = await gamificationService.createMiniGameSession({
        gameType: 'order_blitz',
        tableId: tid,
        sessionIdentifier: myId,
        orderSessionId: parseInt(localStorage.getItem('sessionNumericId')) || null,
      })
      data = res.data
      isHost.value = true
    }
    gameSessionId = data.id
    target.value = data.target || 300
    duration.value = data.duration || 45
    timeLeft.value = data.duration || 45
    players.value = data.players || []
  } catch (e) {
    console.warn('Session error:', e)
    gameSessionId = null
    target.value = 300
    duration.value = 45
    timeLeft.value = 45
  }
}

function startLobbyPolling() {
  stopLobbyPolling()
  lobbyPollInterval = setInterval(async () => {
    if (!gameSessionId || phase.value !== 'lobby') return
    try {
      const { data } = await gamificationService.getMiniGameSession(
        gameSessionId,
      )
      if (!data) return
      players.value = data.players || []
      target.value = data.target || target.value
      duration.value = data.duration || duration.value
      timeLeft.value = data.duration || timeLeft.value
      if (data.status === 'playing') {
        stopLobbyPolling()
        beginPlaying()
      }
    } catch (e) {
      /* retry */
    }
  }, 2000)
}

function stopLobbyPolling() {
  if (lobbyPollInterval) {
    clearInterval(lobbyPollInterval)
    lobbyPollInterval = null
  }
}

async function launchGame() {
  if (!gameSessionId) return
  try {
    await gamificationService.startMiniGame(gameSessionId)
  } catch (e) {
    /* ignore */
  }
  stopLobbyPolling()
  beginPlaying()
}

function beginPlaying() {
  phase.value = 'playing'
  myScore.value = 0
  totalScore.value = 0
  combo.value = 0
  maxCombo.value = 0
  completedOrders.value = 0
  orderCount.value = 0
  gameWon.value = false
  reward.value = null
  lastSyncScore = 0

  newOrder()

  timerInterval = setInterval(() => {
    timeLeft.value--
    if (myScore.value - lastSyncScore >= 20) {
      lastSyncScore = myScore.value
      syncScore()
    }
    if (timeLeft.value <= 0) endGame()
  }, 1000)
}

async function syncScore() {
  totalScore.value = myScore.value
  if (!gameSessionId) return
  try {
    const { data } = await gamificationService.submitMiniGameScore(
      gameSessionId,
      {
        sessionIdentifier: myId,
        score: myScore.value,
        comboBreak: combo.value === 0,
      },
    )
    totalScore.value = data.totalScore || myScore.value
    players.value = data.players || []
    if (data.won) gameWon.value = true
  } catch (e) {
    /* offline fallback */
  }
}

async function endGame() {
  clearInterval(timerInterval)
  await syncScore()
  phase.value = 'result'
  gameWon.value = totalScore.value >= target.value

  if (gameSessionId) {
    try {
      const { data } = await gamificationService.finishMiniGame(gameSessionId)
      reward.value = data.reward
      if (data.reward)
        emit('apply-discount', { ...data.reward, gameName: 'Bestell-Blitz' })
    } catch (e) {
      /* ignore */
    }
  }
}

onMounted(() => {
  if (props.existingGameId) startMultiplayer()
})

onUnmounted(() => {
  clearInterval(timerInterval)
  stopLobbyPolling()
})
</script>

<style scoped>
.blitz-game {
  position: relative;
  width: 100%;
  min-height: 480px;
  background: #0a0a14;
  overflow: hidden;
  border-radius: 1rem;
}

/* ---- Timer ---- */
.timer-row {
  display: flex;
  align-items: center;
  padding: 0.5rem 1rem 0.25rem;
}
.timer-track {
  flex: 1;
  height: 6px;
  border-radius: 99px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.06);
}
.timer-fill {
  height: 100%;
  border-radius: 99px;
  transition: width 0.3s ease;
  background: linear-gradient(90deg, #8b5cf6, #06b6d4);
}
.timer-fill.urgent {
  background: linear-gradient(90deg, #f59e0b, #ef4444);
  animation: urgentPulse 0.5s infinite alternate;
}
.timer-text {
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.75rem;
  font-family: monospace;
  margin-left: 0.5rem;
}
.timer-text.urgent {
  color: #ef4444;
  font-weight: 700;
}

/* ---- HUD ---- */
.hud {
  display: flex;
  justify-content: space-around;
  padding: 0.25rem 0.5rem;
}
.hud-block {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.hud-val {
  font-size: 1.1rem;
  font-weight: 800;
  color: white;
}
.hud-lbl {
  font-size: 9px;
  color: rgba(255, 255, 255, 0.4);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.combo-block.active .hud-val {
  color: #f59e0b;
  animation: comboPulse 0.35s ease;
}

/* ---- Order Card ---- */
.order-card {
  margin: 0.5rem 0.75rem;
  padding: 0.625rem 0.75rem;
  background: rgba(99, 102, 241, 0.08);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 0.75rem;
  transition: all 0.3s ease;
}
.order-card.flash {
  background: rgba(16, 185, 129, 0.15);
  border-color: rgba(16, 185, 129, 0.4);
  animation: orderPop 0.5s ease;
}
.order-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}
.order-title {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.5);
  font-weight: 600;
}
.order-bonus {
  font-size: 0.75rem;
  font-weight: 800;
  color: #10b981;
  animation: bonusFly 0.5s ease forwards;
}
.order-slots {
  display: flex;
  gap: 0.5rem;
  justify-content: center;
  flex-wrap: wrap;
}
.order-slot {
  position: relative;
  width: 3rem;
  height: 3rem;
  background: rgba(255, 255, 255, 0.05);
  border: 2px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.25s ease;
}
.order-slot.done {
  background: rgba(16, 185, 129, 0.15);
  border-color: rgba(16, 185, 129, 0.4);
}
.slot-emoji {
  font-size: 1.4rem;
  transition: opacity 0.2s;
}
.order-slot.done .slot-emoji {
  opacity: 0.35;
}
.slot-check {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  font-weight: 900;
  color: #10b981;
}

/* ---- Food Grid ---- */
.food-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.5rem;
  padding: 0.5rem 0.75rem 0.75rem;
  transition: transform 0.15s ease;
}
.food-grid.shake {
  animation: gridShake 0.3s ease;
}
.food-btn {
  aspect-ratio: 1;
  background: rgba(255, 255, 255, 0.04);
  border: 2px solid rgba(255, 255, 255, 0.08);
  border-radius: 0.875rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.food-btn:active {
  transform: scale(0.9);
}
.food-btn.correct {
  background: rgba(16, 185, 129, 0.2);
  border-color: rgba(16, 185, 129, 0.5);
  animation: correctPop 0.3s ease;
}
.food-btn.wrong {
  background: rgba(239, 68, 68, 0.2);
  border-color: rgba(239, 68, 68, 0.5);
  animation: wrongPop 0.3s ease;
}
.food-btn.used {
  opacity: 0.15;
  pointer-events: none;
  border-color: transparent;
}
.btn-emoji {
  font-size: 1.75rem;
  pointer-events: none;
}

/* ---- Overlays ---- */
.overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 20;
  background: rgba(10, 10, 20, 0.95);
  backdrop-filter: blur(8px);
}

.idle-hero {
  font-size: 4rem;
  margin-bottom: 0.75rem;
  animation: heroFloat 2s ease-in-out infinite alternate;
}
.idle-title {
  font-size: 1.75rem;
  font-weight: 800;
  color: white;
  margin-bottom: 0.25rem;
}
.idle-sub {
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.875rem;
  margin-bottom: 0.25rem;
}
.idle-hint {
  color: rgba(255, 255, 255, 0.35);
  font-size: 0.75rem;
  text-align: center;
  margin-bottom: 1.5rem;
  line-height: 1.4;
}

.btn-row {
  display: flex;
  gap: 0.75rem;
}
.play-btn {
  padding: 0.75rem 1.75rem;
  font-weight: 700;
  border-radius: 0.75rem;
  color: white;
  transition: all 0.2s ease;
  font-size: 0.9rem;
}
.play-btn:active {
  transform: scale(0.95);
}
.play-btn.solo {
  background: linear-gradient(135deg, #8b5cf6, #6366f1);
}
.play-btn.solo:hover {
  background: linear-gradient(135deg, #a78bfa, #818cf8);
}
.play-btn.multi {
  background: linear-gradient(135deg, #06b6d4, #2563eb);
}
.play-btn.multi:hover {
  background: linear-gradient(135deg, #22d3ee, #3b82f6);
}

/* ---- Lobby ---- */
.lobby-icon {
  font-size: 3rem;
  margin-bottom: 0.75rem;
}
.lobby-title {
  font-size: 1.25rem;
  font-weight: 800;
  color: white;
  margin-bottom: 0.25rem;
}
.lobby-sub {
  color: rgba(255, 255, 255, 0.4);
  font-size: 0.75rem;
  margin-bottom: 1rem;
}
.lobby-grid {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.75rem;
  margin: 0.5rem 0;
}
.lobby-chip {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 0.75rem;
  padding: 0.5rem 0.75rem;
}
.chip-avatar {
  width: 2rem;
  height: 2rem;
  border-radius: 9999px;
  background: #6366f1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.875rem;
  font-weight: 700;
}
.chip-avatar.host {
  box-shadow: 0 0 0 2px #fbbf24;
}
.chip-name {
  color: white;
  font-size: 0.875rem;
}
.chip-badge {
  color: #fbbf24;
  font-size: 10px;
  font-weight: 700;
  margin-left: 0.25rem;
}
.lobby-info {
  color: rgba(255, 255, 255, 0.35);
  font-size: 0.75rem;
  margin: 0.75rem 0 1rem;
}
.lobby-start {
  width: 100%;
  max-width: 20rem;
}
.lobby-wait {
  color: rgba(255, 255, 255, 0.4);
  font-size: 0.875rem;
  animation: pulse 1.5s ease-in-out infinite;
}

/* ---- Result ---- */
.result-hero {
  font-size: 4rem;
  margin-bottom: 0.75rem;
  animation: heroFloat 2s ease-in-out infinite alternate;
}
.result-title {
  font-size: 1.5rem;
  font-weight: 800;
  color: white;
  margin-bottom: 1rem;
}
.result-stats {
  display: flex;
  gap: 1.5rem;
  margin-bottom: 1rem;
}
.r-stat {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.r-val {
  font-size: 1.5rem;
  font-weight: 800;
  color: white;
}
.r-lbl {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.4);
  text-transform: uppercase;
}
.reward-card {
  padding: 1rem;
  background: rgba(234, 179, 8, 0.1);
  border: 1px solid rgba(234, 179, 8, 0.3);
  border-radius: 0.75rem;
  text-align: center;
  margin-top: 0.5rem;
}
.reward-text {
  color: #fbbf24;
  font-weight: 700;
  font-size: 0.9rem;
}
.result-close {
  margin-top: 1rem;
}

/* ---- Animations ---- */
@keyframes heroFloat {
  to {
    transform: translateY(-8px);
  }
}
@keyframes urgentPulse {
  to {
    opacity: 0.7;
  }
}
@keyframes comboPulse {
  0% {
    transform: scale(1.3);
  }
  100% {
    transform: scale(1);
  }
}
@keyframes orderPop {
  0% {
    transform: scale(1);
  }
  40% {
    transform: scale(1.03);
  }
  100% {
    transform: scale(1);
  }
}
@keyframes bonusFly {
  0% {
    opacity: 1;
    transform: translateY(0);
  }
  100% {
    opacity: 0;
    transform: translateY(-12px);
  }
}
@keyframes correctPop {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.15);
  }
  100% {
    transform: scale(1);
  }
}
@keyframes wrongPop {
  0% {
    transform: scale(1);
  }
  30% {
    transform: scale(0.9) rotate(-3deg);
  }
  60% {
    transform: scale(0.9) rotate(3deg);
  }
  100% {
    transform: scale(1) rotate(0deg);
  }
}
@keyframes gridShake {
  0%,
  100% {
    transform: translateX(0);
  }
  25% {
    transform: translateX(-4px);
  }
  75% {
    transform: translateX(4px);
  }
}
@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}
</style>
